import { ReactElement, MouseEvent, useMemo, useCallback } from 'react';

import { Nullable } from '~globals/types/commons';
import {
  useAppSelector,
  useSubscriptionFeatures,
  useUIV2State,
} from '~hooks/index';
import { useAccessFeatureModalContext } from '~contexts/AccessFeatureModalContext';
import { selectAuth } from '~store/slices/auth/index';
import { validateRoles } from '~utils/user';
import { validatePartnerCompanyType } from '~utils/partnerCompany';
import { PartnerCompanyTypes } from '~globals/types/enums';
import { NewFeatureBagde, AccessFeature } from '~components/index';
import { PATHS } from '~constants/index';

import { LateralMenuItem } from '../types';

import { classes, LateralMenuTooltip } from '../styles';

import {
  LateralMenuListItemRoot,
  LateralMenuListItemLink,
  LateralMenuListItemLinkTarget,
  LateralMenuListItemIconText,
  LateralMenuListSubItemContainer,
} from './styles';

const LateralMenuListItem = ({
  data,
  tooltipText: tooltipTextProp,
  permissions,
  subItem: SubItemComponent,
}: LateralMenuItem): Nullable<ReactElement> => {
  const { user } = useAppSelector(selectAuth);
  const { handleOpen: handleOpenAccesFeatureModal } =
    useAccessFeatureModalContext();
  const { hasAccessToFeature } = useSubscriptionFeatures();
  const { isEnabledUIV2 } = useUIV2State();

  const LinkComponent = useMemo(
    () =>
      data.target ? LateralMenuListItemLinkTarget : LateralMenuListItemLink,
    [data.target],
  );

  const IconComponent = useMemo(() => data.icon, [data.icon]);

  const isValidFeature = useMemo(
    () =>
      data.chips?.validSubscriptionFeature
        ? hasAccessToFeature(
            data.chips.validSubscriptionFeature.validFeature,
            data.chips.validSubscriptionFeature.availableToDate,
          )
        : true,
    [data.chips?.validSubscriptionFeature, hasAccessToFeature],
  );

  const isEnabled = useMemo(() => {
    const { showOnUIV2, roles, companyType, validateCrossCompanyEnabled } =
      permissions;

    const isEnabledForUIV2 = isEnabledUIV2 ? showOnUIV2 : true;

    const currentRolesPermissionKey = isEnabledUIV2 ? 'UI_V2' : 'UI_V1';

    const currentPermissionsRoles = Array.isArray(roles)
      ? roles
      : roles[currentRolesPermissionKey];

    const isValidRole = validateRoles(user, currentPermissionsRoles);

    const isValidCompanyType = validatePartnerCompanyType(
      user?.company.companyTypeId as PartnerCompanyTypes,
      companyType,
    );

    const isEnabledCrossCompany = validateCrossCompanyEnabled
      ? Boolean(user?.company.crossCompanyEnabled)
      : true;

    return (
      isValidRole &&
      isValidCompanyType &&
      isEnabledCrossCompany &&
      isEnabledForUIV2
    );
  }, [permissions, isEnabledUIV2, user]);

  const tooltipText = useMemo(
    () =>
      typeof tooltipTextProp === 'string'
        ? tooltipTextProp
        : tooltipTextProp(user),
    [tooltipTextProp, user],
  );

  const path = useMemo(() => {
    if (!isValidFeature) return PATHS.HOME.BASE;

    return typeof data.path === 'string' ? data.path : data.path(user);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.path, isValidFeature, user]);

  const handleClickLink = useCallback(
    (event: MouseEvent<HTMLAnchorElement>) => {
      if (!isValidFeature) {
        event.preventDefault();

        handleOpenAccesFeatureModal({
          validFeature:
            data.chips?.validSubscriptionFeature?.validFeature ?? null,
          validateAvailableRoutesState: false,
        });
      }
    },
    [
      data.chips?.validSubscriptionFeature?.validFeature,
      handleOpenAccesFeatureModal,
      isValidFeature,
    ],
  );

  if (!isEnabled) return null;

  return (
    <>
      <LateralMenuTooltip title={tooltipText}>
        <LateralMenuListItemRoot>
          <LinkComponent
            href={path}
            target={data.target}
            to={path}
            color="inherit"
            onClick={handleClickLink}
          >
            <IconComponent className={classes.icon} />

            <LateralMenuListItemIconText
              variant="inherit"
              color="inherit"
              className={classes.iconText}
            >
              {data.title}
            </LateralMenuListItemIconText>

            {data.chips?.activeVersion && (
              <NewFeatureBagde
                activeForVersion={data.chips.activeVersion}
                sx={{ backgroundColor: 'common.white' }}
              />
            )}

            {data.chips?.validSubscriptionFeature && (
              <AccessFeature.NextPlanChip
                {...data.chips.validSubscriptionFeature}
              />
            )}
          </LinkComponent>
        </LateralMenuListItemRoot>
      </LateralMenuTooltip>

      {SubItemComponent && (
        <LateralMenuListSubItemContainer className={classes.subItem}>
          <SubItemComponent />
        </LateralMenuListSubItemContainer>
      )}
    </>
  );
};

export default LateralMenuListItem;
